import type { ProductCatalogEventType } from '../domain/catalogTypes';
import { newClientUuid } from '../../shared/newClientUuid';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { getSessionOrganizationId } from '../../shell/session/sessionOrganization';
import { getOrCreateSyncDeviceId } from '../../sync/core/syncDeviceId';
import { submitCommand } from '../../sync/core/submitCommand';
import { captureOpsFailure } from '../../shared/sentry/sentryOps';

type ProductUpdateSummarySnapshot = {
  name: string;
  price: number;
  sku: string;
  categoryId: string | null;
  categoryName?: string | null;
  barcode?: string | null;
  itemType?: string;
  brandName?: string | null;
  supplierName?: string | null;
  internalNotes?: string | null;
  supplierItemCode?: string | null;
  isActive?: boolean;
};

export const buildProductUpdateSummary = (
  before: ProductUpdateSummarySnapshot,
  after: ProductUpdateSummarySnapshot
) => {
  const parts: string[] = [];
  if (before.name !== after.name) parts.push(`Nombre: «${before.name}» → «${after.name}»`);
  if (before.price !== after.price) parts.push(`Precio: ${before.price} → ${after.price} ARS`);
  if (before.sku !== after.sku) parts.push(`Código: ${before.sku} → ${after.sku}`);
  if (before.categoryId !== after.categoryId) {
    const beforeLabel = before.categoryName?.trim() || '—';
    const afterLabel = after.categoryName?.trim() || '—';
    parts.push(`Categoría: ${beforeLabel} → ${afterLabel}`);
  }
  if ((before.barcode ?? '') !== (after.barcode ?? '')) {
    parts.push(`Código barras: ${before.barcode || '—'} → ${after.barcode || '—'}`);
  }
  if (before.itemType && after.itemType && before.itemType !== after.itemType) {
    parts.push(`Tipo: ${before.itemType} → ${after.itemType}`);
  }
  if ((before.brandName ?? '') !== (after.brandName ?? '')) {
    parts.push(`Marca: ${before.brandName || '—'} → ${after.brandName || '—'}`);
  }
  if ((before.supplierName ?? '') !== (after.supplierName ?? '')) {
    parts.push(`Proveedor: ${before.supplierName || '—'} → ${after.supplierName || '—'}`);
  }
  if ((before.internalNotes ?? '') !== (after.internalNotes ?? '')) {
    parts.push('Notas internas actualizadas');
  }
  if ((before.supplierItemCode ?? '') !== (after.supplierItemCode ?? '')) {
    parts.push(`Cód. producto proveedor: ${before.supplierItemCode || '—'} → ${after.supplierItemCode || '—'}`);
  }
  if (
    before.isActive !== undefined &&
    after.isActive !== undefined &&
    before.isActive !== after.isActive
  ) {
    parts.push(after.isActive ? 'Producto activado' : 'Producto desactivado');
  }
  return parts.join(' · ') || 'Sin cambios relevantes';
};

/**
 * Persiste un evento del historial del producto vía
 * `public.apply_product_catalog_event_command` (entity `product_catalog_event`).
 *
 * Post-effect best-effort: si falla se observa en Sentry (`catalog.event_write`)
 * y no se tira la mutación del producto ya confirmada — el historial nunca
 * bloquea la escritura principal, pero tampoco se traga el error en silencio.
 */
export async function appendProductCatalogEvent(params: {
  productId: string;
  eventType: ProductCatalogEventType;
  summary: string;
  payload: Record<string, unknown>;
  performedByUserId?: string | null;
}): Promise<void> {
  const id = newClientUuid();
  const createdAt = new Date().toISOString();
  const organizationId = await getSessionOrganizationId();
  const performedByUserId =
    params.performedByUserId ?? useAuthStore.getState().session?.user?.id ?? null;

  try {
    const result = await submitCommand({
      entity: 'product_catalog_event',
      operation: 'insert',
      payload: {
        id,
        // El servidor deduplica por (org, 'product_catalog_event', operationId).
        operationId: id,
        organizationId,
        productId: params.productId,
        eventType: params.eventType,
        summary: params.summary,
        payload: params.payload ?? {},
        createdAt,
        performedByUserId,
        originDeviceId: await getOrCreateSyncDeviceId().catch(() => null),
      },
    });
    if (!result.applied) {
      throw new Error(result.ingestError?.trim() || 'product_catalog_event_not_applied');
    }
  } catch (error: unknown) {
    const err = error instanceof Error ? error : new Error(String(error));
    captureOpsFailure({
      module: 'catalog',
      op: 'catalog.event_write',
      error: err,
      ingestError: err.message,
      applied: false,
      itemId: params.productId,
      extra: { eventType: params.eventType, eventId: id },
    });
  }
}
